import Vue from 'vue';
import Router from 'vue-router';
import { routes } from './routerMap';
import store from '../store';
import { i18n } from '../localization';
import { ROUTE_NAMES } from '../constants';
import { unsavedHandler } from '../components/common/helpers';

Vue.use(Router);

const router = new Router({
    routes,
    linkActiveClass: 'active',
});

/**
 * Checks if route can be accessed by given user
 * @param {boolean|null} imt
 * @param {boolean} visibility
 * @param {int} type
 * @param {string} routeName
 * @returns {boolean}
 */
export const checkRouteAvailable = ({ imt, visibility, type }, routeName) =>
    routes.some(
        route =>
            (route.name === routeName ||
                (!!route.children &&
                    route.children.some(
                        route =>
                            route.name === routeName ||
                            (!!route.children && route.children.some(route => route.name === routeName))
                    ))) &&
            !!route.meta &&
            !!route.meta.visibility &&
            route.meta.visibility(imt, visibility, type)
    );

/**
 * Checks if user is allowed to access route and passes to it, otherwise redirects
 * him to 'error-404' page
 * @param {object} user
 * @param {string} routeName
 * @param {Function} next
 */
export function checkAndRedirect(user, routeName, next) {
    if (!!user && !!user.login_name && !checkRouteAvailable(user, routeName)) {
        next('/error-404');
    } else {
        next();
    }
}

/**
 * router global navigation guard (before), checks user permissions and auth
 * @param {object} to
 * @param {object} from
 * @param {Function} next
 */
export function beforeEachGuard(to, from, next) {
    if (store.state.unsavedChanges) {
        let confirmTitle = store.state.isLoading
            ? i18n.t('common.changesLostTitle')
            : i18n.t('common.unsavedTitle');

        let confirmMessage = {
            confirmTitle,
            confirmBody: i18n.t('common.unsavedBody'),
            confirmOk: i18n.t('common.leave'),
            confirmCancel: i18n.t('common.stay'),
        };

        return unsavedHandler(confirmMessage)
            .then(() => {
                store.commit('SET_UNSAVED_CHANGES', false);
                if (to.meta.help !== from.meta.help) {
                    store.dispatch('popup/clearMessages');
                }
                next(true);
            })
            .catch(() => {
                next(false);
            });
    } else if (!store.state.user.authorized) {
        store.dispatch('user/checkIfLogged').then(() => {
            checkAndRedirect(store.state.user.user, to.name, next);
        });
    } else {
        store.dispatch('user/checkToken').then(checkToken => {
            if (checkToken) {
                checkAndRedirect(store.state.user.user, to.name, next);
            } else {
                next(false);
            }
        });
    }
}

router.beforeEach(beforeEachGuard);

router.afterEach((to, from) => store.dispatch('header/setHeader', { to, from }));

router.onReady(() => {
    window.onbeforeunload = () => {
        if (store.state.unsavedChanges) {
            return i18n.t('common.unsavedTitle');
        }
    };
});

/**
 * Creates navigation menu for user logged in
 * @param {object} user
 * @returns {Array}
 */
export function userMenu(user) {
    return routes.filter(
        route =>
            !!route.meta &&
            !!route.meta.visibility &&
            route.meta.menu &&
            route.meta.visibility(user.imt, user.visibility, user.type) &&
            route.name !== ROUTE_NAMES.EXTERNAL
    );
}

export default router;
